/**
 * Value Report page — embedded monthly report.
 *
 * The report itself is rendered server-side by the orchestrator
 * (/value-report.html) because the numbers are computed from Cosmos data.
 * This page fetches it with auth attached (Entra bearer or session cookie)
 * and displays it inside the app, so the report works on both the Static
 * Web App and the Function host — a plain link to /value-report.html only
 * works on the Function host and drops Entra users at a 401, so we embed.
 */
import { useEffect, useMemo, useState } from 'react';
import { API_BASE, apiFetch } from '../api/base';
import { usePortCo } from '../context/PortCoContext';
import { TrendingUp } from 'lucide-react';

const REPORT_HOST = API_BASE.replace(/\/api\/?$/, '');

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function ValueReport() {
  const { selectedPortco, selectedPortcoName } = usePortCo();
  const [month, setMonth] = useState<string>(() => monthKey(new Date()));
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // last 6 months for the selector
  const months = useMemo(() => {
    const out: { key: string; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({
        key: monthKey(d),
        label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
      });
    }
    return out;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ embed: '1', month });
    if (selectedPortco && selectedPortco !== 'all') qs.set('portco', selectedPortco);
    apiFetch(`${REPORT_HOST}/value-report.html?${qs.toString()}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Report unavailable (${res.status})`);
        const text = await res.text();
        if (!cancelled) { setHtml(text); setLoading(false); }
      })
      .catch((e) => {
        if (!cancelled) { setError(e.message || 'Failed to load report'); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [selectedPortco, month]);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center space-x-2.5">
          <TrendingUp className="w-5 h-5 text-mantis-600" />
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Monthly Value Report</h1>
            <p className="text-xs text-slate-500">
              {selectedPortcoName || selectedPortco} — denial recovery, appeal outcomes, and approval activity
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <label htmlFor="vr-month" className="text-xs font-medium text-slate-500">Month</label>
          <select
            id="vr-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-mantis-200"
          >
            {months.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500">
          Generating report…
        </div>
      )}
      {error && !loading && (
        <div className="bg-redwood-50 border border-redwood-200 rounded-xl p-6 text-sm text-redwood-700">
          {error}
        </div>
      )}
      {!loading && !error && (
        <iframe
          title="Value Report"
          srcDoc={html || ''}
          className="w-full bg-white border border-slate-200 rounded-xl"
          style={{ height: 'calc(100vh - 160px)', minHeight: 600 }}
        />
      )}
      <p className="mt-3 text-[11px] text-slate-400">
        Figures reconcile with your billing data. “Recovered” counts only claims whose appeal outcome is recorded as approved.
      </p>
    </div>
  );
}
